import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Plus, Loader2, Star, Check, X, Pencil, Download } from "lucide-react";
import { api, resolveImg, formatPrice, formatApiError } from "@/lib/api";
import { useStore } from "@/context/StoreContext";

const inp = "mt-1 w-full rounded-lg border border-[#E7DFD3] px-3 py-2 text-sm outline-none focus:border-amber-500";
const lbl = "font-mono text-[10px] uppercase tracking-widest text-stone-500";
const card = "rounded-xl border border-[#E7DFD3] bg-white p-4";

/* -------------------- logo / image uploader -------------------- */
function LogoUploader({ label, value, onChange, testid }) {
  const fileRef = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success(`${label} uploaded`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
    setBusy(false);
  };
  return (
    <div>
      <label className={lbl}>{label}</label>
      <div className="mt-1 flex items-center gap-3">
        <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded-full border border-[#E7DFD3] bg-[#FAF7F2]">
          {value ? <img src={resolveImg(value)} alt={label} className="h-full w-full object-contain" /> : <span className="text-[9px] text-stone-400">none</span>}
        </div>
        <div className="flex gap-2">
          <button type="button" data-testid={`${testid}-upload`} onClick={() => fileRef.current?.click()} disabled={busy} className="flex items-center gap-1.5 rounded-lg border border-[#E7DFD3] px-3 py-1.5 text-xs hover:border-amber-500 disabled:opacity-60">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload
          </button>
          {value && <button type="button" onClick={() => onChange("")} className="rounded-lg border border-[#E7DFD3] px-2 py-1.5 text-xs text-red-500 hover:border-red-300"><Trash2 size={13} /></button>}
        </div>
      </div>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
    </div>
  );
}

/* ==================================================== BRAND SETTINGS */
export function BrandPanel() {
  const { settings, loadSettings } = useStore();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (settings && !form) setForm({ ...settings }); }, [settings, form]);
  if (!form) return <div className="mt-6"><Loader2 className="animate-spin text-amber-500" /></div>;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { await api.put("/settings", form); await loadSettings(); toast.success("Brand settings published"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    setSaving(false);
  };
  const LOGOS = [["logo_primary", "Primary Logo"], ["logo_desktop", "Desktop Logo"], ["logo_mobile", "Mobile Logo"], ["logo_light", "Light Logo"], ["logo_dark", "Dark Logo"], ["favicon", "Favicon"], ["og_image", "Social / OG Image"]];
  return (
    <div className="mt-6 space-y-6" data-testid="admin-brand">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-2xl text-stone-900">Brand Settings</h2>
        <button data-testid="brand-save" onClick={save} disabled={saving} className="flex items-center gap-2 rounded-full gold-gradient px-6 py-2.5 text-sm font-semibold uppercase tracking-wider text-stone-900 disabled:opacity-60">
          {saving && <Loader2 size={15} className="animate-spin" />} Publish
        </button>
      </div>

      <div className={card}>
        <h3 className="mb-3 font-serif text-lg text-stone-900">Logos & Favicon</h3>
        <p className="mb-4 text-[11px] text-stone-400">Upload your own files — we always use your exact image, never AI. Pick which logo is active site-wide.</p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {LOGOS.map(([k, label]) => <LogoUploader key={k} label={label} value={form[k]} onChange={(v) => set(k, v)} testid={`brand-${k}`} />)}
        </div>
        <div className="mt-4 max-w-xs">
          <label className={lbl}>Active Logo (site-wide)</label>
          <select data-testid="brand-active-logo" value={form.active_logo} onChange={(e) => set("active_logo", e.target.value)} className={inp}>
            <option value="primary">Primary</option><option value="desktop">Desktop</option><option value="mobile">Mobile</option><option value="light">Light</option><option value="dark">Dark</option>
          </select>
        </div>
      </div>

      <div className={card}>
        <h3 className="mb-3 font-serif text-lg text-stone-900">Identity</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <div><label className={lbl}>Brand Name</label><input data-testid="brand-name" value={form.brand_name} onChange={(e) => set("brand_name", e.target.value)} className={inp} /></div>
          <div><label className={lbl}>Tagline</label><input data-testid="brand-tagline" value={form.tagline} onChange={(e) => set("tagline", e.target.value)} className={inp} /></div>
          <div><label className={lbl}>Primary Font</label><input value={form.font_primary} onChange={(e) => set("font_primary", e.target.value)} className={inp} /></div>
          <div><label className={lbl}>Secondary Font</label><input value={form.font_secondary} onChange={(e) => set("font_secondary", e.target.value)} className={inp} /></div>
          <div><label className={lbl}>Primary Colour</label><input type="text" value={form.color_primary} onChange={(e) => set("color_primary", e.target.value)} className={inp} /></div>
          <div><label className={lbl}>Accent Colour</label><input type="text" value={form.color_accent} onChange={(e) => set("color_accent", e.target.value)} className={inp} /></div>
        </div>
      </div>

      <div className={card}>
        <h3 className="mb-3 font-serif text-lg text-stone-900">Contact & Social</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <div><label className={lbl}>WhatsApp Number (digits, with country code)</label><input data-testid="brand-whatsapp" value={form.whatsapp_number} onChange={(e) => set("whatsapp_number", e.target.value)} className={inp} /></div>
          <div><label className={lbl}>Instagram URL</label><input value={form.instagram_url} onChange={(e) => set("instagram_url", e.target.value)} className={inp} /></div>
          <div><label className={lbl}>Facebook URL</label><input value={form.facebook_url} onChange={(e) => set("facebook_url", e.target.value)} className={inp} /></div>
          <div><label className={lbl}>Pinterest URL</label><input value={form.pinterest_url} onChange={(e) => set("pinterest_url", e.target.value)} className={inp} /></div>
          <div><label className={lbl}>Support Email</label><input value={form.support_email} onChange={(e) => set("support_email", e.target.value)} className={inp} /></div>
          <div><label className={lbl}>Phone</label><input value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inp} /></div>
        </div>
      </div>

      <div className={card}>
        <h3 className="mb-3 font-serif text-lg text-stone-900">Store</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <div><label className={lbl}>Announcement Bar</label><input value={form.announcement} onChange={(e) => set("announcement", e.target.value)} className={inp} /></div>
          <div><label className={lbl}>Free Shipping Threshold (₹)</label><input type="number" value={form.free_shipping_threshold} onChange={(e) => set("free_shipping_threshold", Number(e.target.value))} className={inp} /></div>
        </div>
      </div>
    </div>
  );
}

/* ==================================================== COUPONS */
const EMPTY_COUPON = { code: "", type: "percent", value: 10, min_order: 0, expiry: "", usage_limit: 0, first_order_only: false, active: true };
export function CouponsPanel() {
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState(EMPTY_COUPON);
  const load = () => api.get("/admin/coupons").then(({ data }) => setCoupons(data));
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const create = async () => {
    if (!form.code) return toast.error("Enter a code");
    try { await api.post("/admin/coupons", { ...form, value: Number(form.value), min_order: Number(form.min_order), usage_limit: Number(form.usage_limit) }); toast.success("Coupon created"); setForm(EMPTY_COUPON); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  const del = async (id) => { await api.delete(`/admin/coupons/${id}`); load(); };
  return (
    <div className="mt-6" data-testid="admin-coupons">
      <h2 className="mb-4 font-serif text-2xl text-stone-900">Coupons</h2>
      <div className={`${card} mb-6`}>
        <div className="grid gap-3 sm:grid-cols-3">
          <div><label className={lbl}>Code</label><input data-testid="coupon-code" value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())} className={inp} placeholder="WELCOME10" /></div>
          <div><label className={lbl}>Type</label><select data-testid="coupon-type" value={form.type} onChange={(e) => set("type", e.target.value)} className={inp}><option value="percent">Percentage %</option><option value="flat">Flat ₹</option></select></div>
          <div><label className={lbl}>Value</label><input data-testid="coupon-value" type="number" value={form.value} onChange={(e) => set("value", e.target.value)} className={inp} /></div>
          <div><label className={lbl}>Min Order ₹</label><input type="number" value={form.min_order} onChange={(e) => set("min_order", e.target.value)} className={inp} /></div>
          <div><label className={lbl}>Expiry (YYYY-MM-DD)</label><input value={form.expiry} onChange={(e) => set("expiry", e.target.value)} className={inp} placeholder="2026-12-31" /></div>
          <div><label className={lbl}>Usage Limit (0 = ∞)</label><input type="number" value={form.usage_limit} onChange={(e) => set("usage_limit", e.target.value)} className={inp} /></div>
        </div>
        <label className="mt-3 flex items-center gap-2"><input type="checkbox" checked={form.first_order_only} onChange={(e) => set("first_order_only", e.target.checked)} className="h-4 w-4 accent-amber-600" /><span className="text-sm text-stone-700">First order only</span></label>
        <button data-testid="coupon-create" onClick={create} className="mt-4 flex items-center gap-2 rounded-full gold-gradient px-5 py-2 text-sm font-semibold uppercase tracking-wider text-stone-900"><Plus size={15} /> Create Coupon</button>
      </div>
      <div className="space-y-2">
        {coupons.map((c) => (
          <div key={c.id} data-testid={`coupon-${c.code}`} className={`${card} flex items-center justify-between`}>
            <div>
              <p className="font-mono text-sm font-bold text-stone-900">{c.code}</p>
              <p className="text-xs text-stone-500">{c.type === "percent" ? `${c.value}% off` : `${formatPrice(c.value)} off`}{c.min_order ? ` · min ${formatPrice(c.min_order)}` : ""}{c.expiry ? ` · till ${c.expiry}` : ""}{c.first_order_only ? " · first order" : ""} · used {c.used_count}{c.usage_limit ? `/${c.usage_limit}` : ""}</p>
            </div>
            <button data-testid={`coupon-del-${c.code}`} onClick={() => del(c.id)} className="text-red-400 hover:text-red-600"><Trash2 size={16} /></button>
          </div>
        ))}
        {coupons.length === 0 && <p className="text-stone-500">No coupons yet.</p>}
      </div>
    </div>
  );
}

/* ==================================================== COLLECTIONS */
const HOME_CATEGORIES = ["rings", "necklaces", "earrings", "bracelets", "anklets"];

function CollectionCard({ c, onSaved, onDelete }) {
  const [text, setText] = useState({ tagline: c.tagline || "", image_alt: c.image_alt || "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { setText({ tagline: c.tagline || "", image_alt: c.image_alt || "" }); }, [c.tagline, c.image_alt]);
  const slug = (c.slug || c.name).toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const isCat = HOME_CATEGORIES.includes(c.name.trim().toLowerCase());
  const save = async (patch, msg) => {
    setSaving(true);
    try { await api.put(`/admin/collections/${c.id}`, { ...c, ...text, ...patch }); toast.success(msg); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    setSaving(false);
  };
  const dirty = text.tagline !== (c.tagline || "") || text.image_alt !== (c.image_alt || "");
  return (
    <div data-testid={`collection-item-${slug}`} className={card}>
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="min-w-0"><p className="truncate font-medium text-stone-900">{c.name} {c.featured && "★"}</p><p className="truncate text-xs text-stone-500">{c.description}</p>
          {isCat && <span className="mt-1 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-amber-800">Homepage category card</span>}
        </div>
        <button data-testid={`collection-del-${slug}`} onClick={onDelete} className="text-red-400 hover:text-red-600"><Trash2 size={15} /></button>
      </div>
      <ImgField value={c.image} onChange={(v) => save({ image: v }, v ? `${c.name} image saved` : `${c.name} image removed`)} testid={`collection-${slug}-img`} />
      <div className="mt-3 grid gap-2">
        <div><label className={lbl}>Tagline (small text above the name)</label><input data-testid={`collection-${slug}-tagline`} value={text.tagline} onChange={(e) => setText((t) => ({ ...t, tagline: e.target.value }))} className={inp} placeholder="Encircling Grace" /></div>
        <div><label className={lbl}>Image Alt Text</label><input data-testid={`collection-${slug}-alt`} value={text.image_alt} onChange={(e) => setText((t) => ({ ...t, image_alt: e.target.value }))} className={inp} placeholder={`e.g. Gold-tone ${c.name.toLowerCase()} on beige silk`} /></div>
      </div>
      <button data-testid={`collection-${slug}-save`} onClick={() => save({}, `${c.name} details saved`)} disabled={saving || !dirty} className="mt-3 flex items-center gap-2 rounded-full gold-gradient px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-stone-900 disabled:opacity-40">
        {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Save details
      </button>
    </div>
  );
}
export function CollectionsPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", description: "", image: "", featured: false });
  const fileRef = useRef();
  const load = () => api.get("/collections").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const uploadImg = async (file) => { if (!file) return; const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setForm((f) => ({ ...f, image: data.url })); };
  const create = async () => { if (!form.name) return toast.error("Enter a name"); await api.post("/admin/collections", form); toast.success("Collection created"); setForm({ name: "", description: "", image: "", featured: false }); load(); };
  const del = async (id) => { if (!window.confirm("Delete this collection?")) return; await api.delete(`/admin/collections/${id}`); load(); };
  return (
    <div className="mt-6" data-testid="admin-collections">
      <h2 className="mb-4 font-serif text-2xl text-stone-900">Collections</h2>
      <div className={`${card} mb-6`}>
        <div className="grid gap-3 sm:grid-cols-2">
          <div><label className={lbl}>Name</label><input data-testid="collection-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inp} placeholder="Everyday Edit" /></div>
          <div><label className={lbl}>Description</label><input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className={inp} /></div>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <div className="h-14 w-14 overflow-hidden rounded-lg border border-[#E7DFD3] bg-[#F3EDE2]">{form.image && <img src={resolveImg(form.image)} alt="" className="h-full w-full object-cover" />}</div>
          <button type="button" onClick={() => fileRef.current?.click()} className="rounded-lg border border-[#E7DFD3] px-3 py-1.5 text-xs hover:border-amber-500">Upload image</button>
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.featured} onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))} className="h-4 w-4 accent-amber-600" /><span className="text-sm text-stone-700">Featured</span></label>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImg(e.target.files?.[0])} />
        </div>
        <p className="mt-2 text-[11px] text-stone-400">Assign products to a collection by typing this name in the product's Collection field.</p>
        <button data-testid="collection-create" onClick={create} className="mt-4 flex items-center gap-2 rounded-full gold-gradient px-5 py-2 text-sm font-semibold uppercase tracking-wider text-stone-900"><Plus size={15} /> Create</button>
      </div>
      <p className="mb-3 text-xs text-stone-500">Collections named Rings, Necklaces, Earrings, Bracelets and Anklets power the homepage "Shop by Category" cards — upload, replace or remove their image below (saves instantly), and edit the tagline & alt text then tap Save details.</p>
      <div className="grid gap-3 sm:grid-cols-2">
        {items.map((c) => <CollectionCard key={c.id} c={c} onSaved={load} onDelete={() => del(c.id)} />)}
        {items.length === 0 && <p className="text-stone-500">No collections yet.</p>}
      </div>
    </div>
  );
}

/* ==================================================== REVIEWS */
export function ReviewsPanel() {
  const [revs, setRevs] = useState([]);
  const load = () => api.get("/admin/reviews").then(({ data }) => setRevs(data));
  useEffect(() => { load(); }, []);
  const moderate = async (id, patch) => { await api.put(`/admin/reviews/${id}`, patch); load(); };
  const del = async (id) => { await api.delete(`/admin/reviews/${id}`); load(); };
  return (
    <div className="mt-6" data-testid="admin-reviews">
      <h2 className="mb-4 font-serif text-2xl text-stone-900">Reviews</h2>
      <div className="space-y-3">
        {revs.map((r) => (
          <div key={r.id} data-testid={`review-${r.id}`} className={card}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm text-stone-900">{"★".repeat(r.rating)}<span className="text-stone-300">{"★".repeat(5 - r.rating)}</span> <span className="font-medium">{r.title}</span> {r.verified && <span className="ml-1 rounded bg-green-100 px-1.5 py-0.5 text-[9px] text-green-700">Verified</span>}</p>
                <p className="mt-1 text-sm text-stone-600">{r.body}</p>
                <p className="mt-1 text-xs text-stone-400">{r.customer_name} · {new Date(r.created_at).toLocaleDateString("en-IN")}</p>
              </div>
              <div className="flex flex-shrink-0 gap-2">
                {!r.approved
                  ? <button data-testid={`review-approve-${r.id}`} onClick={() => moderate(r.id, { approved: true })} className="rounded-full bg-green-600 px-3 py-1 text-xs text-white">Approve</button>
                  : <button onClick={() => moderate(r.id, { approved: false })} className="rounded-full border border-[#E7DFD3] px-3 py-1 text-xs">Unpublish</button>}
                <button onClick={() => moderate(r.id, { featured: !r.featured })} className={`rounded-full border px-2 py-1 text-xs ${r.featured ? "border-amber-500 text-amber-600" : "border-[#E7DFD3] text-stone-500"}`}><Star size={12} /></button>
                <button onClick={() => del(r.id)} className="text-red-400 hover:text-red-600"><Trash2 size={15} /></button>
              </div>
            </div>
          </div>
        ))}
        {revs.length === 0 && <p className="text-stone-500">No reviews yet.</p>}
      </div>
    </div>
  );
}

/* ==================================================== ANALYTICS */
export function AnalyticsPanel() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then(({ data }) => setA(data)); }, []);
  if (!a) return <div className="mt-6"><Loader2 className="animate-spin text-amber-500" /></div>;
  const maxRev = Math.max(1, ...a.revenue_by_date.map((d) => d.revenue));
  const KPI = ({ label, value }) => (
    <div className={card}><p className={lbl}>{label}</p><p className="mt-1 font-serif text-2xl text-stone-900">{value}</p></div>
  );
  const List = ({ title, rows, fmt }) => (
    <div className={card}><h3 className="mb-2 font-serif text-lg text-stone-900">{title}</h3>
      {rows.length === 0 && <p className="text-sm text-stone-400">No data yet.</p>}
      {rows.map((r, i) => <div key={i} className="flex justify-between border-b border-[#EFE8DC] py-1.5 text-sm last:border-0"><span className="truncate text-stone-700">{r.name}</span><span className="text-stone-500">{fmt(r)}</span></div>)}
    </div>
  );
  return (
    <div className="mt-6 space-y-5" data-testid="admin-analytics">
      <h2 className="font-serif text-2xl text-stone-900">Analytics</h2>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <KPI label="Total Sales" value={formatPrice(a.total_sales)} />
        <KPI label="Orders" value={a.orders} />
        <KPI label="Avg Order" value={formatPrice(a.aov)} />
        <KPI label="Customers" value={a.total_customers} />
        <KPI label="Returning" value={a.returning_customers} />
        <KPI label="New" value={a.new_customers} />
      </div>
      <div className={card}>
        <h3 className="mb-3 font-serif text-lg text-stone-900">Revenue (last 30 days)</h3>
        <div className="flex h-32 items-end gap-1">
          {a.revenue_by_date.map((d, i) => (
            <div key={i} className="flex-1 rounded-t bg-amber-400/70 transition-all hover:bg-amber-500" style={{ height: `${(d.revenue / maxRev) * 100}%` }} title={`${d.date}: ${formatPrice(d.revenue)}`} />
          ))}
          {a.revenue_by_date.length === 0 && <p className="text-sm text-stone-400">No revenue yet.</p>}
        </div>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <List title="Bestselling" rows={a.bestselling} fmt={(r) => `${r.qty} sold · ${formatPrice(r.revenue)}`} />
        <List title="Most Viewed" rows={a.most_viewed} fmt={(r) => `${r.views} views`} />
        <List title="Most Wishlisted" rows={a.most_wishlisted} fmt={(r) => `${r.count} ♥`} />
        <List title="Low Stock" rows={a.low_stock} fmt={(r) => `${r.stock} left`} />
      </div>
    </div>
  );
}

const RETURN_STATUSES = ["Requested", "Approved", "Rejected", "Pickup Scheduled", "Received", "Refunded", "Exchange Processed"];

export function ReturnsPanel() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/returns").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => { await api.put(`/admin/returns/${id}`, { status }); toast.success(`Return → ${status}`); load(); };
  return (
    <div className="mt-6" data-testid="admin-returns">
      <h2 className="mb-4 font-serif text-2xl text-stone-900">Returns & Exchanges ({rows.length})</h2>
      {rows.length === 0 && <p className="text-stone-500">No return requests yet.</p>}
      <div className="space-y-3">
        {rows.map((r) => (
          <div key={r.id} className={card} data-testid={`admin-return-${r.order_number}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-mono text-sm text-stone-900">{r.order_number} · <span className="uppercase text-amber-700">{r.type}</span></p>
                <p className="text-xs text-stone-500">{r.customer_name} · {r.customer_email} · {r.product_name}</p>
                <p className="mt-1 text-sm text-stone-700">Reason: {r.reason}</p>
              </div>
              <select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)} className="rounded-lg border border-[#E7DFD3] px-3 py-2 text-sm outline-none focus:border-amber-500">
                {RETURN_STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function SectionsManager() {
  const { settings, loadSettings } = useStore();
  const [rows, setRows] = useState([]);
  useEffect(() => { if (settings?.homepage_sections) setRows(settings.homepage_sections); }, [settings]);
  const move = (i, d) => { const a = [...rows]; const j = i + d; if (j < 0 || j >= a.length) return; [a[i], a[j]] = [a[j], a[i]]; setRows(a); };
  const toggle = (i) => { const a = [...rows]; a[i] = { ...a[i], enabled: !a[i].enabled }; setRows(a); };
  const save = async () => { await api.put("/settings", { ...settings, homepage_sections: rows }); toast.success("Homepage layout saved"); loadSettings && loadSettings(); };
  return (
    <div className="mt-10" data-testid="homepage-sections">
      <h3 className="mb-1 font-serif text-xl text-stone-900">Homepage Sections</h3>
      <p className="mb-3 text-xs text-stone-500">Reorder with the arrows and toggle visibility. Changes apply to the live homepage on save.</p>
      <div className="max-w-lg space-y-2">
        {rows.map((s, i) => (
          <div key={s.key} data-testid={`section-row-${s.key}`} className="flex items-center justify-between rounded-xl border border-[#E7DFD3] bg-white px-3 py-2">
            <span className="text-sm text-stone-800">{s.label}</span>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1 text-xs text-stone-500">
                <input type="checkbox" data-testid={`section-enable-${s.key}`} checked={s.enabled !== false} onChange={() => toggle(i)} className="accent-amber-600" /> Show
              </label>
              <button data-testid={`section-up-${s.key}`} onClick={() => move(i, -1)} className="rounded border border-[#E7DFD3] px-2 text-stone-600 hover:border-amber-500">↑</button>
              <button data-testid={`section-down-${s.key}`} onClick={() => move(i, 1)} className="rounded border border-[#E7DFD3] px-2 text-stone-600 hover:border-amber-500">↓</button>
            </div>
          </div>
        ))}
      </div>
      <button data-testid="sections-save" onClick={save} className="mt-4 rounded-full gold-gradient px-6 py-2.5 text-sm font-semibold uppercase tracking-wider text-stone-900">Save Homepage Layout</button>
    </div>
  );
}

/* -------------------- generic image uploader (rectangular) -------------------- */
function ImgField({ value, onChange, testid }) {
  const fileRef = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
    setBusy(false);
  };
  return (
    <div className="flex items-center gap-3">
      <div className="h-16 w-24 flex-shrink-0 overflow-hidden rounded-lg border border-[#E7DFD3] bg-[#F3EDE2]">
        {value && <img src={resolveImg(value)} alt="" className="h-full w-full object-cover" />}
      </div>
      <div className="flex gap-2">
        <button type="button" data-testid={`${testid}-upload`} onClick={() => fileRef.current?.click()} disabled={busy} className="flex items-center gap-1.5 rounded-lg border border-[#E7DFD3] px-3 py-1.5 text-xs hover:border-amber-500 disabled:opacity-60">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload
        </button>
        {value && <button type="button" data-testid={`${testid}-remove`} onClick={() => onChange("")} className="rounded-lg border border-[#E7DFD3] px-2 py-1.5 text-xs text-red-500 hover:border-red-300"><Trash2 size={13} /></button>}
      </div>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
    </div>
  );
}

/* ==================================================== PROMO BANNERS */
const EMPTY_BANNER = { image: "", eyebrow: "", heading: "", subtext: "", cta_label: "", cta_link: "/shop", enabled: true };
export function BannersPanel() {
  const { settings, loadSettings } = useStore();
  const [rows, setRows] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (settings && rows === null) setRows(settings.banners || []); }, [settings, rows]);
  if (rows === null) return <div className="mt-6"><Loader2 className="animate-spin text-amber-500" /></div>;
  const setRow = (i, k, v) => setRows((a) => a.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const add = () => setRows((a) => [...a, { ...EMPTY_BANNER }]);
  const del = (i) => setRows((a) => a.filter((_, idx) => idx !== i));
  const move = (i, d) => setRows((a) => { const b = [...a]; const j = i + d; if (j < 0 || j >= b.length) return a; [b[i], b[j]] = [b[j], b[i]]; return b; });
  const save = async () => {
    setSaving(true);
    try { await api.put("/settings", { ...settings, banners: rows }); await loadSettings(); toast.success("Promo banners published"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    setSaving(false);
  };
  return (
    <div className="mt-6 space-y-5" data-testid="admin-banners">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-2xl text-stone-900">Promo Banners</h2>
        <button data-testid="banners-save" onClick={save} disabled={saving} className="flex items-center gap-2 rounded-full gold-gradient px-6 py-2.5 text-sm font-semibold uppercase tracking-wider text-stone-900 disabled:opacity-60">
          {saving && <Loader2 size={15} className="animate-spin" />} Publish
        </button>
      </div>
      <p className="text-xs text-stone-500">Add promotional banner blocks for the homepage. Reorder here, and control where the whole "Promo Banners" block sits in the Content → Homepage Sections tab.</p>
      {rows.length === 0 && <p className="text-stone-500">No banners yet. Add your first below.</p>}
      <div className="space-y-4">
        {rows.map((b, i) => (
          <div key={i} className={card} data-testid={`banner-row-${i}`}>
            <div className="mb-3 flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Banner {i + 1}</span>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1 text-xs text-stone-500"><input type="checkbox" checked={b.enabled !== false} onChange={(e) => setRow(i, "enabled", e.target.checked)} className="accent-amber-600" /> Show</label>
                <button onClick={() => move(i, -1)} className="rounded border border-[#E7DFD3] px-2 text-stone-600 hover:border-amber-500">↑</button>
                <button onClick={() => move(i, 1)} className="rounded border border-[#E7DFD3] px-2 text-stone-600 hover:border-amber-500">↓</button>
                <button data-testid={`banner-del-${i}`} onClick={() => del(i)} className="text-red-400 hover:text-red-600"><Trash2 size={15} /></button>
              </div>
            </div>
            <div className="mb-3"><label className={lbl}>Background Image</label><div className="mt-1"><ImgField value={b.image} onChange={(v) => setRow(i, "image", v)} testid={`banner-${i}-img`} /></div></div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div><label className={lbl}>Eyebrow (small label)</label><input data-testid={`banner-${i}-eyebrow`} value={b.eyebrow} onChange={(e) => setRow(i, "eyebrow", e.target.value)} className={inp} placeholder="Limited Time" /></div>
              <div><label className={lbl}>Heading</label><input data-testid={`banner-${i}-heading`} value={b.heading} onChange={(e) => setRow(i, "heading", e.target.value)} className={inp} placeholder="Festive Edit — up to 30% off" /></div>
              <div className="sm:col-span-2"><label className={lbl}>Subtext</label><input value={b.subtext} onChange={(e) => setRow(i, "subtext", e.target.value)} className={inp} placeholder="Shop the new anti-tarnish arrivals" /></div>
              <div><label className={lbl}>Button Label</label><input value={b.cta_label} onChange={(e) => setRow(i, "cta_label", e.target.value)} className={inp} placeholder="Shop Now" /></div>
              <div><label className={lbl}>Button Link (/shop or https://…)</label><input value={b.cta_link} onChange={(e) => setRow(i, "cta_link", e.target.value)} className={inp} placeholder="/shop?segment=Oxidised" /></div>
            </div>
          </div>
        ))}
      </div>
      <button data-testid="banner-add" onClick={add} className="flex items-center gap-2 rounded-full border border-[#E7DFD3] px-5 py-2 text-sm font-semibold uppercase tracking-wider text-stone-700 hover:border-amber-500"><Plus size={15} /> Add Banner</button>
    </div>
  );
}

/* ==================================================== TESTIMONIALS */
const EMPTY_TESTIMONIAL = { name: "", location: "", quote: "", image: "", rating: 5, featured: false };
export function TestimonialsPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_TESTIMONIAL);
  const [editId, setEditId] = useState(null);
  const load = () => api.get("/admin/testimonials").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const reset = () => { setForm(EMPTY_TESTIMONIAL); setEditId(null); };
  const submit = async () => {
    if (!form.name || !form.quote) return toast.error("Name and quote are required");
    try {
      const payload = { ...form, rating: Number(form.rating) };
      if (editId) await api.put(`/admin/testimonials/${editId}`, payload);
      else await api.post("/admin/testimonials", payload);
      toast.success(editId ? "Testimonial updated" : "Testimonial added");
      reset(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  const edit = (t) => { setForm({ name: t.name, location: t.location, quote: t.quote, image: t.image, rating: t.rating, featured: t.featured }); setEditId(t.id); };
  const del = async (id) => { await api.delete(`/admin/testimonials/${id}`); if (editId === id) reset(); load(); };
  return (
    <div className="mt-6" data-testid="admin-testimonials">
      <h2 className="mb-4 font-serif text-2xl text-stone-900">Testimonials</h2>
      <div className={`${card} mb-6`}>
        <h3 className="mb-3 font-serif text-lg text-stone-900">{editId ? "Edit testimonial" : "Add testimonial"}</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <div><label className={lbl}>Customer Name</label><input data-testid="testimonial-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inp} placeholder="Ananya S." /></div>
          <div><label className={lbl}>Location</label><input data-testid="testimonial-location" value={form.location} onChange={(e) => set("location", e.target.value)} className={inp} placeholder="Mumbai, India" /></div>
        </div>
        <div className="mt-3"><label className={lbl}>Quote</label><textarea data-testid="testimonial-quote" value={form.quote} onChange={(e) => set("quote", e.target.value)} rows={3} className={inp} placeholder="The packaging felt like unwrapping a first-class gift…" /></div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div>
            <label className={lbl}>Rating</label>
            <select data-testid="testimonial-rating" value={form.rating} onChange={(e) => set("rating", Number(e.target.value))} className={inp}>
              {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{`${n} ★`}</option>)}
            </select>
          </div>
          <div><label className={lbl}>Photo (optional)</label><div className="mt-1"><ImgField value={form.image} onChange={(v) => set("image", v)} testid="testimonial-img" /></div></div>
        </div>
        <label className="mt-3 flex items-center gap-2"><input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="h-4 w-4 accent-amber-600" /><span className="text-sm text-stone-700">Featured (shown first)</span></label>
        <div className="mt-4 flex gap-2">
          <button data-testid="testimonial-submit" onClick={submit} className="flex items-center gap-2 rounded-full gold-gradient px-5 py-2 text-sm font-semibold uppercase tracking-wider text-stone-900">{editId ? <Check size={15} /> : <Plus size={15} />} {editId ? "Update" : "Add"}</button>
          {editId && <button onClick={reset} className="rounded-full border border-[#E7DFD3] px-5 py-2 text-sm text-stone-600">Cancel</button>}
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((t) => (
          <div key={t.id} data-testid={`testimonial-item-${t.id}`} className={card}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-full border border-[#E7DFD3] bg-[#F3EDE2]">{t.image ? <img src={resolveImg(t.image)} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center font-serif text-sm text-amber-700">{(t.name || "?").charAt(0)}</div>}</div>
                <div><p className="font-medium text-stone-900">{t.name} {t.featured && <span className="text-amber-500">★</span>}</p><p className="text-xs text-stone-500">{t.location}</p></div>
              </div>
              <div className="flex flex-shrink-0 gap-2">
                <button onClick={() => edit(t)} className="text-stone-500 hover:text-amber-600"><Pencil size={15} /></button>
                <button onClick={() => del(t.id)} className="text-red-400 hover:text-red-600"><Trash2 size={15} /></button>
              </div>
            </div>
            <p className="mt-2 text-sm text-stone-600 line-clamp-3">“{t.quote}”</p>
            <p className="mt-1 text-amber-400">{"★".repeat(t.rating || 5)}</p>
          </div>
        ))}
        {items.length === 0 && <p className="text-stone-500">No testimonials yet.</p>}
      </div>
    </div>
  );
}

/* ==================================================== PAYMENTS (Razorpay) */
export function PaymentsPanel() {
  const [cfg, setCfg] = useState(null);
  const [keyId, setKeyId] = useState("");
  const [secret, setSecret] = useState("");
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/admin/payments").then(({ data }) => { setCfg(data); setKeyId(data.razorpay_key_id || ""); });
  useEffect(() => { load(); }, []);
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/admin/payments", { razorpay_key_id: keyId, razorpay_key_secret: secret }); setCfg(data); setSecret(""); toast.success("Payment settings saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    setSaving(false);
  };
  if (!cfg) return <div className="mt-6"><Loader2 className="animate-spin text-amber-500" /></div>;
  return (
    <div className="mt-6 space-y-5" data-testid="admin-payments">
      <h2 className="font-serif text-2xl text-stone-900">Payments</h2>
      <div className={card}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-serif text-lg text-stone-900">Razorpay (Online Payments)</h3>
          <span data-testid="razorpay-status" className={`rounded-full px-3 py-1 text-xs font-semibold ${cfg.enabled ? "bg-green-100 text-green-700" : "bg-stone-100 text-stone-500"}`}>{cfg.enabled ? "Live" : "Not connected"}</span>
        </div>
        <p className="mb-4 text-[11px] text-stone-400">Enter your Razorpay API keys to enable online card/UPI payments at checkout. Until keys are added, checkout continues via WhatsApp. Get keys from Razorpay Dashboard → Settings → API Keys. Your secret is stored securely and never shown again.</p>
        <div className="grid max-w-xl gap-4">
          <div><label className={lbl}>Razorpay Key ID</label><input data-testid="razorpay-key-id" value={keyId} onChange={(e) => setKeyId(e.target.value)} className={inp} placeholder="rzp_live_xxxxxxxx" /></div>
          <div><label className={lbl}>Razorpay Key Secret {cfg.has_secret && <span className="text-green-600">(saved — leave blank to keep)</span>}</label><input data-testid="razorpay-key-secret" type="password" value={secret} onChange={(e) => setSecret(e.target.value)} className={inp} placeholder={cfg.has_secret ? "••••••••" : "Enter secret key"} /></div>
        </div>
        <button data-testid="payments-save" onClick={save} disabled={saving} className="mt-4 flex items-center gap-2 rounded-full gold-gradient px-6 py-2.5 text-sm font-semibold uppercase tracking-wider text-stone-900 disabled:opacity-60">{saving && <Loader2 size={15} className="animate-spin" />} Save Keys</button>
      </div>
    </div>
  );
}

/* ==================================================== DATA EXPORT */
export function ExportPanel() {
  const [range, setRange] = useState({ from: "", to: "" });
  const [busy, setBusy] = useState("");
  const download = async (entity, format) => {
    const key = `${entity}-${format}`;
    setBusy(key);
    try {
      const params = { format };
      if (entity === "orders") { if (range.from) params.date_from = range.from; if (range.to) params.date_to = range.to; }
      const res = await api.get(`/admin/export/${entity}`, { params, responseType: "blob" });
      const name = /filename="([^"]+)"/.exec(res.headers["content-disposition"] || "")?.[1] || `priyaraa-${entity}.${format}`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${entity.charAt(0).toUpperCase() + entity.slice(1)} exported`);
    } catch (e) { toast.error("Export failed"); }
    setBusy("");
  };
  const row = (entity, title, desc, children = null) => (
    <div className={card} data-testid={`export-${entity}`}>
      <h3 className="font-serif text-lg text-stone-900">{title}</h3>
      <p className="mb-3 text-xs text-stone-500">{desc}</p>
      {children}
      <div className="flex flex-wrap gap-2">
        {[["csv", "Download CSV"], ["xlsx", "Download Excel"]].map(([f, l]) => (
          <button key={f} data-testid={`export-${entity}-${f}`} onClick={() => download(entity, f)} disabled={!!busy} className="flex items-center gap-2 rounded-full border border-[#E7DFD3] bg-white px-4 py-2 text-xs font-semibold uppercase tracking-wider text-stone-700 hover:border-amber-500 disabled:opacity-50">
            {busy === `${entity}-${f}` ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} {l}
          </button>
        ))}
      </div>
    </div>
  );
  return (
    <div className="mt-6 max-w-3xl space-y-4" data-testid="admin-export">
      <h2 className="font-serif text-2xl text-stone-900">Export Data</h2>
      <p className="text-xs text-stone-500">Download spreadsheets of your store data. CSV opens in Excel & Google Sheets; Excel downloads a .xlsx file.</p>
      {row("products", "Products", "Name, SKU, category, finish, prices, stock, status and tags.")}
      {row("orders", "Orders", "Order number, date, customer, items, totals, payment and delivery details. Leave dates empty to export all orders.", (
        <div className="mb-3 grid max-w-sm grid-cols-2 gap-3">
          <div><label className={lbl}>From</label><input type="date" data-testid="export-orders-from" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} className={inp} /></div>
          <div><label className={lbl}>To</label><input type="date" data-testid="export-orders-to" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} className={inp} /></div>
        </div>
      ))}
      {row("customers", "Customers", "Name, email, phone, number of orders, total spent and join date.")}
    </div>
  );
}
